import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  ApiJobListCards,
  ApiBreadcrumbs,
  buildSearchParams,
  fetcher,
  firstQueryValue,
  ApiBreadcrumbsOk,
  isJobListCardsOk,
  isBreadcrumbsOk,
  ObjectFilterParams,
} from '@utils/data-fetching';
import {
  useEffect, ReactNode, useMemo, useState,
} from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Button, LinkStyled } from '@components/common';
import { BellIcon } from '@heroicons/react/24/solid';
import { MapPinIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '@store/index';
import {
  paginatedServerSideQuery,
  PaginatedServerSideQueryResponse,
} from '@utils/infinite-scrolling/processedServerSideQueries';
import { JobSeoWidget } from '@components/templates/job/SeoWidget';
import { JobSearchFilters } from '@components/features/filters/JobSearchFilters';
import { useFiltersQueryParamsJobList } from '@hooks/filters/useFiltersQueryParamsJobList';
import { countAppliedFilters } from '@components/features/filters/helpers/countAppliedFilters';
import { clsx } from 'clsx';
import { useCreateJobSearchAlert } from '@hooks/features/useCreateJobSearchAlert';
import { JobListType } from '@type/job-params';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import dynamic from 'next/dynamic';

import { Breadcrumbs } from '@components/templates/job/Breadcrumbs';
import { gsspCheckCookieAuth, gsspGetLocale } from '@utils/gssp';
import { JobCardList } from '@components/templates/job/Card/JobCardList';
import { buildJobListUrl, getJobListMetaDescription, getJobListParametersFromSlug, getJobListTitle } from '@utils/job';
import type { CandidateSearchFilters } from '@components/contexts/CandidateNavigationContext';
import { getCanonicalUrl } from '@services/i18nPaths';
import { useRouter } from 'next/router';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import { GuestLegalBar } from '@components/layout/GuestLegalBar';
import { CandidatePromptBar } from '@components/features/candidate-prompt-bar';
import { useSetHeaderSearchContext } from '@components/contexts/HeaderSearchContext';
import { getJobListHeadTitle } from '@utils/job/buildJobListHeadTitle';
import { useSwrJobListBreadcrumbs, useSwrJobListCardsInfinite } from '@hooks/useSwrApi/endpoints/job';
import { FiltersObjectInterface } from '@type/form-types';
import { JOB_LIST_ITEMS_PER_PAGE, SAVE_ALERT_PARAM } from 'src/constants';
import { getServerSideTranslation } from '@app/i18n';
import ogImageEn from '@images/social-media/ai_on_your_side_en.png';
import ogImageRo from '@images/social-media/ai_on_your_side_ro.png';
import { useSwrJobSearchSeo } from '@root/src/hooks/useSwrApi/endpoints/job/useSwrJobSearchSeo';
import { useFilterDrawer } from '@components/contexts/FilterDrawerContext';
import { useJobListLocationRestore } from '@hooks/useJobListLocationRestore';
import type { BulkApplyModalProps } from '@components/templates/job/apply/BulkApplyModal';
import { JobListRouteProvider } from '@components/contexts/JobListRouteContext';
import type { JobSearchQueryParams, FiltersQueryParamsStates } from '@type/filters';

const JobAlertCard = dynamic(() => import('@components/templates/job/list/JobAlertCard').then((mod) => mod.JobAlertCard));

// Job Alert banner positions (how many cards to be displayed before the banner)
const JOB_ALERT_BANNER_POSITIONS = [12, 24] as const;

// Position (1-based) of the My Bestie promo card in the grid. Always on the first page
// (< JOB_LIST_ITEMS_PER_PAGE) so it never duplicates across pages or shifts pagination.

// Interface
// ******************************************
interface JobListPageProps {
  locale: Locale,
  infiniteQueryParams: PaginatedServerSideQueryResponse,
  jobListCardsFromServer: ApiSchema<'JobResult'> | null,
  breadcrumbsFromServer: ApiBreadcrumbsOk | null,
  seoDataFromServer: ApiSchema<'SeoData'> | null,
  isAuthenticated: boolean,
  ipCityFromHeader?: string | null,
}


// Api keys that need to be an array. The thing is, form multiples (checkbox, select) return a string when only one value
// is selected, and array when multiple values are selected. The API expects arrays for these keys, even if only one value is selected.
// ******************************************
const apiKeysAsArray = ['domain', 'careerLevels', 'employmentTypes', 'spokenLanguages', 'benefits'];

/**
 * Parse x-client-citylatlong header to extract latitude and longitude
 * @param headerValue - The header value in format "lat,lon"
 * @returns Object with _lat and _lon or null if invalid
 */
const parseLocationHeader = (headerValue: string | undefined): { _lat: string; _lon: string } | null => {
  if (!headerValue) return null;
  const parts = headerValue.split(',');
  if (parts.length !== 2) return null;
  const [lat, lon] = parts.map((part) => part.trim());
  // Basic validation - check if they're numeric
  if (!lat || !lon || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) return null;
  // Basic range validation for latitude and longitude
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (latNum < -90 || latNum > 90 || lonNum < -180 || lonNum > 180) return null;
  return { _lat: lat, _lon: lon };
};

/**
 * @description Job List page
 * - **locale**: string - the app locale.
 * - **infiniteQueryParams**: PaginatedServerSideQueryResponse - the object that contains the query params for the infinite scrolling.
 * - **jobListCardsFromServer**: ApiSchema<'JobResult'> | null - the job list data fetched from the server.
 * - **breadcrumbs**: ApiBreadcrumbsOk | null - the breadcrumb object fetched from the server.
 * - **isAuthenticated**: boolean - a boolean that indicates whether the user is authenticated.
 */
// The SEO widget (jobs-by-city / domain links — looks like a small footer) is hidden on the
// job-list home as of 2026-06-19. To re-enable it, flip this to `true`. Everything else (the
// `useSwrJobSearchSeo` fetch + `JobSeoWidget` render) is left in place so it just reappears.
const SHOW_SEO_WIDGET = false;

const JobListPage = (props: JobListPageProps) => {
  // Destructure props
  const {
    locale,
    infiniteQueryParams,
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
    isAuthenticated,
    ipCityFromHeader,
  } = props;

  // Destructure paginatedQuery. Fallback to {} because Next.js can transiently deliver
  // pageProps without this field during shallow Router.push transitions between
  // catchall slug values (e.g. /locuri-de-munca → /locuri-de-munca/hr). Downstream
  // hooks already treat each field as optional.
  const {
    cursor,
    limit,
    objFilterParams,
  } = infiniteQueryParams ?? {};

  // Translation
  const { t } = useTranslation('common');

  // Track if Google Maps should be loaded (lazy loading trigger)

  // Handle query params in browser URL, history and filtering
  // ***********************************************
  const {
    queryParams,
    setQueryParams,
    setCurrentPage,
  } = useFiltersQueryParamsJobList({
    serverQueryParams: objFilterParams,
    initialPage: cursor,
    t,
    paginationType: 'cursor',
  }) as FiltersQueryParamsStates<JobSearchQueryParams> & { setCurrentPage: Dispatch<SetStateAction<string | number>> };

  // Next router
  const { asPath, push: routerPush } = useRouter();

  // Prompt-bar search → navigate to the SEO job-list URL for the typed keyword / locations.
  // `saveAlert=1` and the keyword as typed ride the *internal* href, never the `as` URL: the address bar
  // keeps the clean SEO slug, so a reload or a shared link — carrying neither — saves no alert. The
  // server render below is then the single request that may save one, and since the flag never reaches
  // the client's cache key, nothing the list does afterwards (revalidation, paging) can repeat it.
  const handlePromptSearch = (filters: CandidateSearchFilters) => {
    const url = buildJobListUrl(t, locale, {
      ...(filters.keyword ? { keyword: filters.keyword } : {}),
      ...(filters.locations?.length ? { location: filters.locations } : {}),
    });
    if (!url) return;

    // Keyword-only: the API auto-saves an alert only for a search that has one (see
    // JobSearchManager::saveJobSearchAndConfigureHeaders), so marking a location-only search would send
    // a flag it ignores.
    const href = filters.keyword
      ? `${url}?${SAVE_ALERT_PARAM}=1&keyword=${encodeURIComponent(filters.keyword)}`
      : url;
    void routerPush(href, url);
  };

  // Fetch data
  const {
    data,
    size,
    setSize,
    isLoading,
    isEmpty,
    sizeAsRef,
    isValidating,
    showLoadMore,
  } = useSwrJobListCardsInfinite({
    locale,
    limit,
    serverSideData: jobListCardsFromServer ?? undefined,
    initialCursor: cursor,
    // Pass the query params to the hook
    urlParams: buildSearchParams(queryParams, '&', false, apiKeysAsArray),
  });

  // Hook: capture/restore coords and force refetch when coords become available
  useJobListLocationRestore({
    queryParams,
    setQueryParams,
    setCurrentPage,
    setSize,
  });

  const lastGroup = (data && data.length) ? data[data.length - 1] : undefined;
  const apiRequestId = lastGroup?.apiRequestId;

  const { data: seoData } = useSwrJobSearchSeo({
    locale,
    serverSideData: seoDataFromServer,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
  });

  // SWR: Fetch breadcrumbs
  // ***********************************************
  const { data: breadcrumbs } = useSwrJobListBreadcrumbs({
    locale,
    shouldFetch: !isAuthenticated,
    urlParams: buildSearchParams(queryParams, '?', false, apiKeysAsArray),
    serverSideData: breadcrumbsFromServer ?? undefined,
  });

  // Push queryParams and setQueryParams to the HeaderSearchContext provider (lives above the layout in _app.tsx).
  // This allows header components (FilterButton, SearchJobSection) to access the search context.
  // Cleanup on unmount is handled automatically by the hook.
  // ***********************************************
  useSetHeaderSearchContext({ queryParams, setQueryParams });

  // Helpers
  // ***********************************************
  const firstGroup = (data && data.length) ? data[0] : undefined;
  const searchId = firstGroup?.searchId;
  const searchAlertExists = firstGroup?.jobSearchAlertExists;

  const locArr = queryParams?.location ?? undefined;
  const hasManualLocation = Array.isArray(locArr) && locArr.length > 0;

  const showLoadingOverlay = useMemo(
    () => isValidating && !isLoading,
    [isValidating, isLoading],
  );

  // Filter labels state
  // ***********************************************
  const [filterLabels, setFilterLabels] = useState<FiltersObjectInterface | undefined>(undefined);

  // Metadata
  // *****************************************************
  const title = getJobListTitle(t, filterLabels);
  const headTitle = getJobListHeadTitle(t, locale, title);
  const metaDescription = getJobListMetaDescription(t, filterLabels);

  const ogUrl = getCanonicalUrl(asPath);
  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID ?? '';
  const localizedOgImage = locale === 'ro' ? ogImageRo : ogImageEn;
  const ogImageUrl = localizedOgImage.src.startsWith('http') ? localizedOgImage.src : `${process.env.NEXT_PUBLIC_BASE_URL}${localizedOgImage.src}`;

  // Create job search alert hook
  // *****************************************************
  const {
    createJobSearchAlert,
    isCreatingJobSearchAlert,
    isSavedJobSearchAlert,
  } = useCreateJobSearchAlert({
    searchId: searchId || '',
    alertExists: !!searchAlertExists,
  });

  // Bulk Apply Feed (from REDUX)
  // *****************************************
  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void (async () => {
        const { showBulkApplyModal } = await import('@components/templates/job/apply/BulkApplyModal');
        await showBulkApplyModal({ jobList: bulkApplyFeed, locale } satisfies BulkApplyModalProps);
      })();
    }
  }, [bulkApplyFeed, locale]);

  const hasAppliedFilters: boolean = useMemo( // Optionally add the explicit type here for clarity
    () => !!((queryParams as Partial<JobSearchQueryParams>)?.keyword || countAppliedFilters(queryParams) > 0),
    [queryParams],
  );

  // IP-based city label from the header (no client-side geolocation/geocoding)
  const cityToDisplay = ipCityFromHeader ?? null;

  // Open the filters drawer and command the filters hub to open LocationForm
  const { openDrawer, setActiveSection } = useFilterDrawer();
  const openLocationFilter = () => {
    // Mark the Location section as active and then open the drawer
    setActiveSection('location');
    openDrawer();
  };

  // Ensure manual text location and coords don't conflict: if user sets a manual location, clear coords
  useEffect(() => {
    const locArr2 = queryParams?.location ?? undefined;
    const hasManualLocation2 = Array.isArray(locArr2) && locArr2.length > 0;
    const hasCoords = Boolean(queryParams?._lat || queryParams?._lon);

    if (hasManualLocation2 && hasCoords) {
      setCurrentPage('');
      setQueryParams((prev) => {
        if (prev && typeof prev === 'object') {
          const { _lat: omitLat, _lon: omitLon, ...rest } = prev;
          return rest;
        }
        return {};
      });
    }
  }, [queryParams?.location, queryParams?._lat, queryParams?._lon, setQueryParams, setCurrentPage]);

  // Safety: if only one of _lat/_lon is present, remove both to avoid malformed queries
  useEffect(() => {
    const hasLat = Boolean(queryParams?._lat);
    const hasLon = Boolean(queryParams?._lon);
    if (hasLat !== hasLon) {
      setQueryParams((prev) => {
        const { _lat: omittedLat, _lon: omittedLon, ...rest } = prev;
        return rest;
      });
    }
  }, [queryParams?._lat, queryParams?._lon, setQueryParams]);

  // Optional normalization: remove empty location arrays so we don't send an empty key
  useEffect(() => {
    const locArr3 = queryParams?.location ?? undefined;
    if (Array.isArray(locArr3) && locArr3.length === 0) {
      setQueryParams((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const { location: omittedLocation, ...rest } = prev as JobSearchQueryParams;
        return rest;
      });
    }
  }, [queryParams?.location, setQueryParams]);

  // Render page component
  // *****************************************************
  const pageContent = (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>{headTitle}</title>
        <meta name="description" content={metaDescription} />

        {/* Open Graph */}
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={headTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="fb:app_id" content={facebookAppId} />
        <meta property="og:image" content={ogImageUrl} />
      </Head>

      {/* PAGE */}
      <div className="min-h-screen bg-surface-100 md:min-h-3/4-screen">

        {/* Filters */}
        <JobSearchFilters queryParams={queryParams} setQueryParams={setQueryParams} setFilterLabels={setFilterLabels} />

        <div className="container relative mt-1">
          {/* Breadcrumbs */}
          {breadcrumbs && hasAppliedFilters && <Breadcrumbs className="mb-1" breadcrumbs={breadcrumbs} />}

          <div className="mb-2 mt-4 flex items-start justify-between gap-3">
            {/* Location info — stacks on two rows on mobile so it can share the row with the applications link */}
            <div className="inline-flex flex-col items-start gap-2 text-sm text-ink sm:flex-row sm:items-center">
              {!hasManualLocation && cityToDisplay && (
                <button
                  type="button"
                  onClick={openLocationFilter}
                  data-test-id="job-list-location"
                  className={clsx(
                    'inline-flex min-w-0 cursor-pointer items-center gap-2',
                    'text-ink hover:text-primary',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                    'underline-offset-2 hover:underline',
                  )}
                  aria-label={t('global.location.label')}
                >
                  <MapPinIcon aria-hidden="true" className="size-4 text-ink-light" />
                  <strong className="truncate font-semibold">{cityToDisplay}</strong>
                </button>
              )}

            </div>

            {/* My applications — right-aligned; guests go through login with a return target */}
            <LinkStyled
              href={isAuthenticated
                ? '/applied-jobs'
                : `/login?targetPath=${encodeURIComponent(`/${locale}/applied-jobs`)}`}
              styling="text"
              size="sm"
              color="ink"
              rounding="none"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm font-semibold !no-underline hover:!text-primary"
            >
              <BriefcaseIcon aria-hidden="true" className="size-4 text-ink-light" />
              {t('header.menu.applied_jobs')}
            </LinkStyled>
          </div>

          {/* Total items + Save job search */}
          <div className="mb-2 flex flex-col items-start justify-between gap-4 sm:flex-row sm:gap-0">
            {searchId && (
              <Button
                rounding="full"
                size="xs"
                className={clsx('shrink-0 !bg-surface-100 !text-sm !text-pink hover:!bg-pink/10 hover:!shadow-none', isSavedJobSearchAlert && 'pointer-events-none')}
                isLoading={isCreatingJobSearchAlert}
                spinnerClass="w-4 h-4 -ml-1 mr-2"
                onClick={createJobSearchAlert}
              >
                {!isCreatingJobSearchAlert && <BellIcon className="-ml-1 mr-2 size-4" />}
                { isSavedJobSearchAlert ? t('saved.job.alert') : t('save.job.alert') }
              </Button>
            )}
          </div>

          {/* Infinite list */}
          <JobCardList
            data={data}
            trackingData={{
              listType: 'search',
              listUuid: apiRequestId,
            }}
            t={t}
            setCurrentPage={setCurrentPage as Dispatch<SetStateAction<string>>}
            isLoading={isLoading}
            size={size}
            setSize={setSize}
            sizeAsRef={sizeAsRef}
            showLoadMore={showLoadMore}
            isEmpty={isEmpty}
            isValidating={isValidating}
            listType={JobListType.SEARCH_LIST_APISEARCH}
            locale={locale}
            renderBanner={(jobPosition) => {
              const showJobAlert = searchId && JOB_ALERT_BANNER_POSITIONS.includes(jobPosition as typeof JOB_ALERT_BANNER_POSITIONS[number]);

              if (!showJobAlert) return null;

              return (
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  {showJobAlert && (
                    <JobAlertCard
                      createFn={createJobSearchAlert}
                      isSaved={isSavedJobSearchAlert}
                      isSaving={isCreatingJobSearchAlert}
                    />
                  )}
                </div>
              );
            }}
            title={title}
            paginationType="cursor"
          />

          {/* SEO Widget - Rendered only Server-Side - no need to update in client.
              Hidden on the job-list home via SHOW_SEO_WIDGET (flip the flag to re-enable). */}
          {SHOW_SEO_WIDGET && seoData && (seoData.seoCities || seoData.seoDomains) && (
            <JobSeoWidget
              seoCities={seoData.seoCities}
              seoDomains={seoData.seoDomains}
              seoIsAbroad={seoData.seoIsAbroad}
            />
          )}

          {/* Loading Blurred Overlay */}
          {showLoadingOverlay && (
            <div className="absolute inset-0 z-2 cursor-wait backdrop-blur-none lg:backdrop-blur-xs" />
          )}
        </div>
      </div>
    </>
  );


  return (
    <>
      {pageContent}
      <CandidatePromptBar
        locale={locale}
        isAuthenticated={isAuthenticated}
        modeOverride="search"
        onSearchSubmit={handlePromptSearch}
        sticky
      />
      {/* Legal links must stay SSR'd on the guest job list (Google OAuth requirement), so they
          live in the page flow, not inside the bar. */}
      {!isAuthenticated && <GuestLegalBar />}
    </>
  );
};

// Page layout
// ************************************************************************************
JobListPage.getLayout = function getLayout(page: ReactNode) {
  return (
    <JobListRouteProvider>
      <LayoutDefault>
        {page}
      </LayoutDefault>
    </JobListRouteProvider>
  );
};

// GSSP API
// query and params are needed for the initial request used by the infinite scrolling
// ************************************************************************************
export const getServerSideProps: GetServerSideProps<JobListPageProps> = async (context) => {
  // Destructure context
  const {
    req,
    query,
  } = context;

  const locale = gsspGetLocale(query);

  // Server side translations
  const { t } = await getServerSideTranslation(locale, 'common');

  // Process the query to get the search params
  // query is like: { slug: [ 'full-time', 'beneficii', 'bonus-de-performanta', 'germana' ] }
  const {
    slug,
  } = query;

  const filterParams = getJobListParametersFromSlug(slug, t);

  // A keyword in the query string is the authoritative one — it is how a submit passes what the user
  // actually typed (see handlePromptSearch) and how this page's own navigations carry it forward. The
  // slug is only the fallback: lower-cased, diacritics stripped, its own separators indistinguishable
  // from hyphens the user typed. This text is what a saved alert ends up displaying.
  const keywordFromQuery = firstQueryValue(query.keyword);
  if (keywordFromQuery) {
    filterParams.keyword = keywordFromQuery;
  }

  // Only a navigation the user's own search caused carries this, so only that render may save an alert.
  const isSubmittedSearch = firstQueryValue(query[SAVE_ALERT_PARAM]) === '1';

  // Process the query for the infinite scrolling. Use the returned object to pass parameters
  // to useSwrTalentSearchV2 hook and filters.
  const infiniteQueryParams = paginatedServerSideQuery({
    query,
    pageSlug: 'cursor',
    itemsPerPage: JOB_LIST_ITEMS_PER_PAGE,
    paginationType: 'cursor',
  });

  // Add location fallback from loadbalancer header if not already present
  const locationFromHeader = parseLocationHeader(req.headers['x-client-citylatlong'] as string);
  if (locationFromHeader && !filterParams._lat && !filterParams._lon && !query._lat && !query._lon) {
    filterParams._lat = locationFromHeader._lat;
    filterParams._lon = locationFromHeader._lon;
  }

  infiniteQueryParams.objFilterParams = filterParams as ObjectFilterParams;
  infiniteQueryParams.stringFilterParams = buildSearchParams(filterParams, '');

  // Remove any keys from fetchUrlParams that filterParams will provide, to prevent
  // duplicates like domain=9&domain%5B%5D=9 when query-string and slug encode the same filter.
  // Runs whenever there are params at all: the internal marker below has to come out even when the
  // slug carried no filters, or it would reach the breadcrumb and SEO calls and be sent twice below.
  const filterKeys = Object.keys(filterParams);
  if (infiniteQueryParams.fetchUrlParams) {
    const existingParams = new URLSearchParams(infiniteQueryParams.fetchUrlParams.slice(1));
    filterKeys.forEach((key) => {
      existingParams.delete(key);
      existingParams.delete(`${key}[]`);
    });
    // Only the job search request below has any use for it.
    existingParams.delete(SAVE_ALERT_PARAM);
    const cleaned = existingParams.toString();
    infiniteQueryParams.fetchUrlParams = cleaned ? `?${cleaned}` : '';
  }
  infiniteQueryParams.fetchUrlParams += buildSearchParams(filterParams, '&');

  // Check if the user is authenticated
  // *****************************************
  const isAuthenticated = await gsspCheckCookieAuth({ context }) as boolean;

  // Fetch data from server
  // *****************************************
  const [
    jobListCardsFromServer,
    breadcrumbsFromServer,
    seoDataFromServer,
  ] = await Promise.all([

    fetcher<ApiJobListCards>(`/v2/jobs${infiniteQueryParams.fetchUrlParams}${isSubmittedSearch ? `&${SAVE_ALERT_PARAM}=1` : ''}`, locale, {
      ssrClientHeaders: req.headers,
    }),
    !isAuthenticated ? fetcher<ApiBreadcrumbs>(`/v1/jobs/breadcrumb${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }) : Promise.resolve(),
    fetcher<ApiSchema<'SeoData'>>(`/v2/jobs/seo-data${infiniteQueryParams.fetchUrlParams}`, locale, {
      ssrClientHeaders: req.headers,
    }),
  ]);

  // Return props
  // *********************************
  // Extract IP-detected city name from header for banner display
  const ipCityFromHeaderRaw = context.req.headers['x-client-city'] as string | undefined;
  const ipCityFromHeader = typeof ipCityFromHeaderRaw === 'string'
    ? (ipCityFromHeaderRaw.split(',')[0].trim() || null)
    : null;

  return ({
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      infiniteQueryParams,
      jobListCardsFromServer: isJobListCardsOk(jobListCardsFromServer) ? jobListCardsFromServer : null,
      breadcrumbsFromServer: isBreadcrumbsOk(breadcrumbsFromServer) ? breadcrumbsFromServer : null,
      seoDataFromServer: seoDataFromServer || null,
      isAuthenticated,
      ipCityFromHeader,
    },
  });
};

// Page export
// ************************************************************************************
export default JobListPage;
